import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { WallsModels } from '../../../models/walls.model';
import { AuthActions } from '../auth/auth.action-types';
import { OrgWallsActions } from './org-walls.actions';
import { OrgWallEntry } from './org-walls.models';

export interface OrgWallsState extends EntityState<OrgWallEntry> {
  loaded: boolean;
  isLoading: boolean;
  query: WallsModels.GetOrgWallsQuery;
  latestTs: number | null;
  isLastPage: boolean;
  error: string | null;
}

export const orgWallsAdapter = createEntityAdapter<OrgWallEntry>({
  selectId: (wall) => wall.id,
});

const initialQuery: WallsModels.GetOrgWallsQuery = {};

const initialState: OrgWallsState = orgWallsAdapter.getInitialState({
  loaded: false,
  isLoading: false,
  query: initialQuery,
  latestTs: null,
  isLastPage: false,
  error: null,
});

export const orgWallsReducer = createReducer(
  initialState,
  on(OrgWallsActions.resetOrgWalls, () => ({
    ...initialState,
  })),
  on(AuthActions.logout, () => ({
    ...initialState,
  })),
  on(OrgWallsActions.loadOrgWalls, (state) => ({
    ...state,
    error: null,
  })),
  on(OrgWallsActions.getNextPage, (state) => ({
    ...state,
    error: null,
  })),
  on(OrgWallsActions.loadOrgWallsRequest, (state, { query }) => ({
    ...state,
    isLoading: true,
    error: null,
    query,
  })),
  on(OrgWallsActions.loadOrgWallsSuccess, (state, { walls, query }) => {
    const isFirstPage = query.latestTs === undefined;
    const isLastPage = walls.length < (query.limit ?? WallsModels.ORG_WALLS_PAGE_LIMIT);
    const newLatestTs =
      walls.length > 0
        ? walls[walls.length - 1].createdAt
        : isFirstPage
          ? null
          : state.latestTs;

    const nextState: OrgWallsState = {
      ...state,
      loaded: true,
      isLoading: false,
      query,
      latestTs: newLatestTs,
      isLastPage,
      error: null,
    };

    return isFirstPage
      ? orgWallsAdapter.setAll(walls, nextState)
      : orgWallsAdapter.addMany(walls, nextState);
  }),
  on(OrgWallsActions.loadOrgWallsFail, (state, { error }) => ({
    ...state,
    loaded: true,
    isLoading: false,
    error,
  })),
);

export const orgWallsFeature = createFeature({
  name: 'orgWalls',
  reducer: orgWallsReducer,
  extraSelectors: ({ selectOrgWallsState }) => ({
    ...orgWallsAdapter.getSelectors(selectOrgWallsState),
    selectLoaded: createSelector(
      selectOrgWallsState,
      (state: OrgWallsState) => state.loaded,
    ),
    selectIsLoading: createSelector(
      selectOrgWallsState,
      (state: OrgWallsState) => state.isLoading,
    ),
    selectQuery: createSelector(
      selectOrgWallsState,
      (state: OrgWallsState) => state.query,
    ),
    selectLatestTs: createSelector(
      selectOrgWallsState,
      (state: OrgWallsState) => state.latestTs,
    ),
    selectIsLastPage: createSelector(
      selectOrgWallsState,
      (state: OrgWallsState) => state.isLastPage,
    ),
    selectError: createSelector(
      selectOrgWallsState,
      (state: OrgWallsState) => state.error,
    ),
  }),
});
